import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import BackOfficeLayout from '../../components/backoffice/BackOfficeLayout';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Plus, Loader2, CreditCard, CheckCircle, XCircle,
  Info, ExternalLink, Wifi, WifiOff, ChevronDown, ChevronUp,
} from 'lucide-react';
import { toast } from '../../lib/safeToast';

const METHOD_COLORS = {
  GET: 'bg-blue-100 text-blue-800 border-blue-200',
  POST: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  PUT: 'bg-amber-100 text-amber-800 border-amber-200',
  PATCH: 'bg-orange-100 text-orange-800 border-orange-200',
  DELETE: 'bg-red-100 text-red-800 border-red-200',
};

export default function BackOfficeConfigPG() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [gateways, setGateways] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newCode, setNewCode] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [adding, setAdding] = useState(false);

  // API Info dialog state
  const [apiInfoOpen, setApiInfoOpen] = useState(false);
  const [apiInfoLoading, setApiInfoLoading] = useState(false);
  const [apiInfoData, setApiInfoData] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});

  const fetchGateways = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/backoffice/pg-config');
      setGateways(res.data?.data || []);
    } catch {
      toast.error('Failed to load payment gateways');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchGateways(); }, [fetchGateways]);

  const handleAdd = async () => {
    const code = newCode.trim().toLowerCase().replace(/[^a-z0-9_-]/g, '');
    if (!code || !newName.trim()) {
      toast.error('Gateway name and code are required');
      return;
    }
    if (gateways.some((g) => g.gateway_code === code)) {
      toast.error('A gateway with this code already exists');
      return;
    }
    setAdding(true);
    try {
      await api.put(`/backoffice/pg-config/${code}`, {
        name: newName.trim(),
        description: newDesc.trim(),
        is_active: true,
        sandbox: { base_url: '', callback_url: '', username: '', access_key: '', secret_key: '', public_key: '', webhook_secret: '', encryption_method: '' },
        production: { base_url: '', callback_url: '', username: '', access_key: '', secret_key: '', public_key: '', webhook_secret: '', encryption_method: '' },
      });
      toast.success(`Gateway "${newName.trim()}" added`);
      setAddOpen(false);
      setNewName('');
      setNewCode('');
      setNewDesc('');
      await fetchGateways();
    } catch {
      toast.error('Failed to add gateway');
    } finally {
      setAdding(false);
    }
  };

  const handleApiInfo = async (e, gwCode) => {
    e.stopPropagation();
    setApiInfoLoading(true);
    setApiInfoData(null);
    setApiInfoOpen(true);
    setExpandedCategories({});
    try {
      const res = await api.get(`/backoffice/pg-config/${gwCode}/api-info`);
      setApiInfoData(res.data);
      const expanded = {};
      (res.data?.categories || []).forEach((c) => { expanded[c.name] = true; });
      setExpandedCategories(expanded);
    } catch {
      toast.error('Failed to load API info');
      setApiInfoOpen(false);
    } finally {
      setApiInfoLoading(false);
    }
  };

  const toggleCategory = (name) => {
    setExpandedCategories((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const hasSandbox = (gw) => !!(gw.sandbox?.base_url);
  const hasProd = (gw) => !!(gw.production?.base_url);

  return (
    <BackOfficeLayout>
      <div className="space-y-8">
        <div className="flex items-center justify-between pb-4">
          <div>
            <h1 className="text-2xl font-bold text-[#0F172A]">Payment Gateway Configuration</h1>
            <p className="text-slate-600 mt-2">Manage payment gateway integrations and API credentials</p>
          </div>
          <Button
            onClick={() => setAddOpen(true)}
            className="bg-[#0B6445] hover:bg-[#095b3d] text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Gateway
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-[#0B6445]" />
          </div>
        ) : gateways.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <CreditCard className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500 text-lg font-medium">No payment gateways configured</p>
              <p className="text-slate-400 text-sm mt-1">Click &quot;Add Gateway&quot; to set up your first payment gateway integration.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {gateways.map((gw) => (
              <Card
                key={gw.gateway_code}
                className="cursor-pointer hover:shadow-lg hover:border-[#0B6445]/30 transition-all group"
                onClick={() => navigate(`/backoffice/config/pg/${gw.gateway_code}`)}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 bg-[#E6F7F3] rounded-xl flex items-center justify-center shrink-0">
                        <CreditCard className="w-5 h-5 text-[#0B6445]" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#0F172A] group-hover:text-[#0B6445] transition-colors">
                          {gw.name || gw.gateway_code}
                        </h3>
                        <p className="text-xs text-slate-400 font-mono">{gw.gateway_code}</p>
                      </div>
                    </div>
                    <button
                      onClick={(e) => handleApiInfo(e, gw.gateway_code)}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-slate-500 hover:text-[#0B6445] hover:bg-[#E6F7F3] border border-slate-200 hover:border-[#0B6445]/30 transition-colors shrink-0"
                      title="View API Integration Info"
                    >
                      <Info className="w-3.5 h-3.5" />
                      API Info
                    </button>
                  </div>

                  {gw.description && (
                    <p className="text-sm text-slate-500 mt-3 line-clamp-2">{gw.description}</p>
                  )}

                  <div className="flex items-center gap-4 mt-4 pt-4 border-t border-slate-100">
                    <div className="flex items-center gap-1.5 text-xs">
                      {hasSandbox(gw) ? (
                        <CheckCircle className="w-3.5 h-3.5 text-amber-500" />
                      ) : (
                        <XCircle className="w-3.5 h-3.5 text-slate-300" />
                      )}
                      <span className={hasSandbox(gw) ? 'text-amber-700' : 'text-slate-400'}>Sandbox</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs">
                      {hasProd(gw) ? (
                        <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                      ) : (
                        <XCircle className="w-3.5 h-3.5 text-slate-300" />
                      )}
                      <span className={hasProd(gw) ? 'text-emerald-700' : 'text-slate-400'}>Production</span>
                    </div>
                    <div className="ml-auto">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        gw.is_active !== false
                          ? 'bg-emerald-50 text-emerald-700'
                          : 'bg-slate-100 text-slate-500'
                      }`}>
                        {gw.is_active !== false ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Add Gateway Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#0F172A]">
              <CreditCard className="w-5 h-5 text-[#0B6445]" />
              Add Payment Gateway
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              Create a new payment gateway configuration. You can set up API credentials after creating.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-medium text-slate-600 uppercase tracking-wide">Gateway Name</Label>
              <Input
                value={newName}
                onChange={(e) => {
                  setNewName(e.target.value);
                  if (!newCode || newCode === newName.trim().toLowerCase().replace(/[^a-z0-9]/g, '_')) {
                    setNewCode(e.target.value.trim().toLowerCase().replace(/[^a-z0-9]/g, '_'));
                  }
                }}
                placeholder="e.g. Doku, Stripe, PayPal"
                className="input-field"
                autoComplete="off"
              />
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-600 uppercase tracking-wide">Gateway Code</Label>
              <Input
                value={newCode}
                onChange={(e) => setNewCode(e.target.value.trim().toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
                placeholder="e.g. doku, stripe"
                className="input-field font-mono"
                autoComplete="off"
              />
              <p className="text-xs text-slate-400 mt-1">Unique identifier (lowercase, no spaces)</p>
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-600 uppercase tracking-wide">Description</Label>
              <Input
                value={newDesc}
                onChange={(e) => setNewDesc(e.target.value)}
                placeholder="Short description of this gateway"
                className="input-field"
                autoComplete="off"
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setAddOpen(false)} className="border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-slate-900">
              Cancel
            </Button>
            <Button
              onClick={handleAdd}
              disabled={adding || !newName.trim() || !newCode.trim()}
              className="bg-[#0B6445] hover:bg-[#095b3d] text-white"
            >
              {adding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Add Gateway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* API Info Dialog */}
      <Dialog open={apiInfoOpen} onOpenChange={setApiInfoOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] flex flex-col">
          {apiInfoLoading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-[#0B6445]" />
            </div>
          ) : apiInfoData ? (
            <>
              <DialogHeader className="shrink-0">
                <DialogTitle className="flex items-center gap-2 text-[#0F172A]">
                  <Info className="w-5 h-5 text-[#0B6445]" />
                  {apiInfoData.gateway} — API Integration Status
                </DialogTitle>
                <DialogDescription asChild>
                  <div className="space-y-3 pt-2">
                    {/* Connection status summary */}
                    <div className="flex flex-wrap gap-3">
                      <div className="flex items-center gap-2 text-sm">
                        {apiInfoData.connected ? (
                          <Wifi className="w-4 h-4 text-emerald-600" />
                        ) : (
                          <WifiOff className="w-4 h-4 text-red-500" />
                        )}
                        <span className={apiInfoData.connected ? 'text-emerald-700 font-medium' : 'text-red-600 font-medium'}>
                          {apiInfoData.connected ? 'Connected' : 'Not Connected'}
                        </span>
                      </div>
                      <span className="text-slate-300">|</span>
                      <span className="text-sm text-slate-600">
                        <strong>{apiInfoData.total_endpoints}</strong> endpoints integrated
                      </span>
                      {apiInfoData.auth_method && (
                        <>
                          <span className="text-slate-300">|</span>
                          <span className="text-sm text-slate-500">Auth: {apiInfoData.auth_method}</span>
                        </>
                      )}
                    </div>

                    {/* Sandbox / Production status */}
                    <div className="flex gap-4">
                      <div className="flex items-center gap-1.5">
                        {apiInfoData.sandbox?.configured ? (
                          <CheckCircle className="w-4 h-4 text-amber-500" />
                        ) : (
                          <XCircle className="w-4 h-4 text-slate-300" />
                        )}
                        <span className="text-sm text-slate-600">
                          Sandbox: {apiInfoData.sandbox?.configured
                            ? (apiInfoData.sandbox?.has_credentials ? 'Configured' : 'URL only')
                            : 'Not configured'}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {apiInfoData.production?.configured ? (
                          <CheckCircle className="w-4 h-4 text-emerald-500" />
                        ) : (
                          <XCircle className="w-4 h-4 text-slate-300" />
                        )}
                        <span className="text-sm text-slate-600">
                          Production: {apiInfoData.production?.configured
                            ? (apiInfoData.production?.has_credentials ? 'Configured' : 'URL only')
                            : 'Not configured'}
                        </span>
                      </div>
                    </div>

                    {apiInfoData.docs_url && (
                      <a
                        href={apiInfoData.docs_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-sm text-[#0B6445] hover:underline"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                        API Documentation
                      </a>
                    )}
                  </div>
                </DialogDescription>
              </DialogHeader>

              {/* Endpoint list */}
              <div className="overflow-y-auto flex-1 pr-1 -mr-1 mt-4">
                {apiInfoData.categories?.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Info className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                    <p>{apiInfoData.message || 'No API catalog available for this gateway yet.'}</p>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {apiInfoData.categories.map((cat) => (
                      <div key={cat.name} className="border border-slate-200 rounded-lg overflow-hidden">
                        <button
                          onClick={() => toggleCategory(cat.name)}
                          className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
                        >
                          <div className="flex items-center gap-3">
                            <span className="font-semibold text-sm text-[#0F172A]">{cat.name}</span>
                            <Badge variant="outline" className="text-xs font-normal border-slate-300 text-slate-500">
                              {cat.endpoints.length} endpoint{cat.endpoints.length !== 1 ? 's' : ''}
                            </Badge>
                          </div>
                          {expandedCategories[cat.name] ? (
                            <ChevronUp className="w-4 h-4 text-slate-400" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-slate-400" />
                          )}
                        </button>
                        {expandedCategories[cat.name] && (
                          <div className="divide-y divide-slate-100">
                            {/* Table header */}
                            <div className="grid grid-cols-[1fr_70px_1fr_70px] gap-2 px-4 py-2 bg-white border-t border-slate-200">
                              <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Feature</span>
                              <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Method</span>
                              <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Endpoint</span>
                              <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider text-right">Status</span>
                            </div>
                            {cat.endpoints.map((ep, idx) => (
                              <div
                                key={idx}
                                className="grid grid-cols-[1fr_70px_1fr_70px] gap-2 px-4 py-2.5 bg-white hover:bg-slate-50 items-center"
                              >
                                <span className="text-sm text-[#0F172A]">{ep.feature}</span>
                                <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded border text-center ${METHOD_COLORS[ep.method] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                                  {ep.method}
                                </span>
                                <span className="text-xs text-slate-500 font-mono truncate" title={ep.path}>{ep.path}</span>
                                <span className="text-right">
                                  <span className="text-[10px] font-medium text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">
                                    Active
                                  </span>
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <DialogFooter className="shrink-0 pt-4 border-t border-slate-100">
                <Button
                  variant="outline"
                  onClick={() => setApiInfoOpen(false)}
                  className="border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-slate-900"
                >
                  Close
                </Button>
              </DialogFooter>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </BackOfficeLayout>
  );
}
